import { expand } from 'uri-template-lite';
import { apiClient } from '../../common/ApiClient';
import { AccessGroupUrl } from './AccessGroupUrl';
import { createApiQueryHook } from '../../common/queryClient/createApiQueryHook';
import { AccessGroupFilter, AccessGroupListDto } from '../schema/types';
import {
  AccessGroupOptionsPageSchema,
  AccessGroupPageSchema,
} from '../schema/AccessGroupSchema';
import {
  AUTOCOMPLETE_PAGE_SIZE,
  OptionsPage,
  PageRequestParams,
} from '../../common/queryClient';
import { QueryFunctionContext } from '@tanstack/react-query';
import { useInfiniteSearchQuery } from '../../common/queryClient/useInfiniteSearchQuery';

const getAccessGroups = (params: AccessGroupFilter & PageRequestParams) =>
  apiClient
    .get<AccessGroupListDto>(expand(AccessGroupUrl.SEARCH, params))
    .then((response) => AccessGroupPageSchema.parse(response.data));

export const useAccessGroupsQuery = createApiQueryHook(
  ['access-groups'],
  getAccessGroups,
);

const searchAccessGroupOptions = ({
  meta,
  pageParam,
}: QueryFunctionContext<any, number>) =>
  apiClient
    .get<AccessGroupListDto>(
      expand(AccessGroupUrl.SEARCH, {
        ...meta,
        name: (meta?.searchText ?? '') as string,
        page: pageParam ?? 0,
        size: AUTOCOMPLETE_PAGE_SIZE,
        sort: 'name,asc',
      }),
    )
    .then((response) => AccessGroupOptionsPageSchema.parse(response.data));

export const useInfiniteSearchAccessGroupsQuery = (term: string) =>
  useInfiniteSearchQuery(
    ['access-groups-autocomplete'],
    searchAccessGroupOptions,
    term,
  );

interface AccessGroupIdSearchOptions {
  selectedIds?: number[];
}

const searchAccessGroupIdOptions = async ({
  meta,
  pageParam,
}: QueryFunctionContext<any, number>): Promise<OptionsPage> => {
  const response = await apiClient.get<AccessGroupListDto>(
    expand(AccessGroupUrl.SEARCH, {
      name: (meta?.searchText ?? '') as string,
      selectedIds: meta?.selectedIds as number[] | undefined,
      page: pageParam ?? 0,
      size: AUTOCOMPLETE_PAGE_SIZE,
      sort: 'name,asc',
    }),
  );
  const page = AccessGroupPageSchema.parse(response.data);

  return {
    content: page.content.map((item) => ({
      value: item.id,
      label: item.name,
    })),
    last: page.last,
    number: page.number,
  };
};

export const useInfiniteSearchAccessGroupIdsQuery = (
  term: string,
  options?: AccessGroupIdSearchOptions,
) =>
  useInfiniteSearchQuery(
    ['access-group-ids-autocomplete'],
    searchAccessGroupIdOptions,
    term,
    options,
  );
